(() => {
  'use strict';
  const setup = () => {
    window.VNASearchPanel?.destroy();
    const panel = document.getElementById('VnaSearchPanel');
    if (!panel) return;
    const controller = new AbortController();
    const { signal } = controller;
    const body = document.body;
    const form = panel.querySelector('form');
    const input = form.querySelector('input[type="search"]');
    const triggers = '[data-search-menu], [data-search-mobile], [data-search-sticky-form]';
    const backdrop = document.querySelector('.vna-search-backdrop');
    const closeLabel = panel.querySelector('[data-vna-search-close]').getAttribute('aria-label');
    const desktopMenu = matchMedia('(min-width: 1280px) and (hover: hover)');
    let opened = false;
    let suspended = false;
    let hoveredMenu = null;
    let resumeTimer = 0;
    let resumeInputFocus = false;
    let previousFocus = null;
    let bodyStyle = null;
    let topFrame = 0;
    let focusFrame = 0;
    let inertStates = [];
    const originalAttributes = new Map();

    const updateTriggers = () => {
      document.querySelectorAll(triggers).forEach(trigger => {
        if (!originalAttributes.has(trigger)) {
          originalAttributes.set(trigger, {
            expanded: trigger.getAttribute('aria-expanded'),
            controls: trigger.getAttribute('aria-controls'),
            label: trigger.getAttribute('aria-label')
          });
        }
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('aria-expanded', String(opened && !suspended));
        if (!trigger.querySelector('[data-vna-search-toggle-close]')) {
          const icon = document.createElement('span');
          icon.setAttribute('data-vna-search-toggle-close', '');
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML = '<svg viewBox="0 0 24 24"><path d="m5 5 14 14M5 19 19 5"/></svg>';
          trigger.appendChild(icon);
        }
      });
    };

    const syncTop = () => {
      topFrame = 0;
      const header = [...document.querySelectorAll('.section-header-navigation, .section-header-mobile')]
        .find(element => getComputedStyle(element).display !== 'none' && element.getBoundingClientRect().height > 0);
      const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty('--vna-search-header-bottom', `${Math.round(bottom)}px`);
    };
    const scheduleTop = () => {
      if (!topFrame) topFrame = requestAnimationFrame(syncTop);
    };

    const focusInput = () => {
      cancelAnimationFrame(focusFrame);
      // Visibility must enter its open state before the animated input can focus.
      focusFrame = requestAnimationFrame(() => {
        focusFrame = requestAnimationFrame(() => {
          if (opened && !suspended) input.focus({ preventScroll: true });
        });
      });
    };

    const setSuspended = (value) => {
      value = opened && value;
      clearTimeout(resumeTimer);
      if (suspended === value) return;
      suspended = value;
      if (suspended) {
        resumeInputFocus = panel.contains(document.activeElement);
        cancelAnimationFrame(focusFrame);
      }
      body.classList.toggle('vna-search-suspended', suspended);
      panel.inert = suspended;
      panel.setAttribute('aria-hidden', String(suspended));
      backdrop.setAttribute('aria-hidden', String(suspended));
      document.querySelectorAll(triggers).forEach(trigger => {
        trigger.setAttribute('aria-expanded', String(!suspended));
      });
      if (!suspended && resumeInputFocus) {
        const active = document.activeElement;
        // Do not move focus away from a navigation link the visitor is using.
        if (!active || active === body || active === previousFocus || panel.contains(active)) focusInput();
        resumeInputFocus = false;
      }
    };

    const menuFrom = (target) => {
      if (!(target instanceof Element)) return null;
      const item = target.closest('.menu-lv-1');
      return item?.closest('.section-header-navigation #HeaderNavigation') &&
        item.querySelector('.header__submenu, .menu-dropdown') &&
        !item.classList.contains('vna-menu-dismissed') ? item : null;
    };
    const scheduleResume = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        if (hoveredMenu?.isConnected && hoveredMenu.matches(':hover') && desktopMenu.matches) return;
        hoveredMenu = null;
        if (opened) setSuspended(false);
      }, 120);
    };

    const close = (restoreFocus = true) => {
      if (!opened) return;
      opened = false;
      suspended = false;
      hoveredMenu = null;
      resumeInputFocus = false;
      clearTimeout(resumeTimer);
      cancelAnimationFrame(focusFrame);
      body.classList.remove('vna-search-open', 'vna-search-suspended');
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
      backdrop.setAttribute('aria-hidden', 'true');
      inertStates.forEach(([element, wasInert]) => { element.inert = wasInert; });
      inertStates = [];
      if (bodyStyle) {
        body.style.overflow = bodyStyle.overflow;
        body.style.paddingRight = bodyStyle.paddingRight;
        bodyStyle = null;
      }
      document.querySelectorAll(triggers).forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
        const original = originalAttributes.get(trigger);
        if (original) original.label === null ? trigger.removeAttribute('aria-label') : trigger.setAttribute('aria-label', original.label);
      });
      if (restoreFocus) {
        const target = previousFocus?.isConnected && previousFocus.getClientRects().length
          ? previousFocus
          : [...document.querySelectorAll(triggers)].find(element => element.getClientRects().length);
        target?.focus({ preventScroll: true });
      }
    };

    const open = (trigger) => {
      if (opened) return;
      previousFocus = trigger || document.activeElement;
      previousFocus?.focus({ preventScroll: true });
      syncTop();
      opened = true;
      bodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
      const scrollbarWidth = Math.max(0, innerWidth - document.documentElement.clientWidth);
      if (scrollbarWidth) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + scrollbarWidth}px`;
      body.style.overflow = 'hidden';
      inertStates = [...document.querySelectorAll('#MainContent, .wrapper-footer, .halo-toolbar-mobile')]
        .map(element => [element, element.inert]);
      inertStates.forEach(([element]) => { element.inert = true; });
      panel.inert = false;
      panel.setAttribute('aria-hidden', 'false');
      backdrop.setAttribute('aria-hidden', 'false');
      body.classList.add('vna-search-open');
      document.querySelectorAll(triggers).forEach(element => {
        element.setAttribute('aria-expanded', 'true');
        element.setAttribute('aria-label', closeLabel);
      });
      focusInput();
    };

    // Keep the same search session while an existing desktop submenu is hovered.
    document.addEventListener('pointerover', event => {
      if (!opened || !desktopMenu.matches || event.pointerType === 'touch') return;
      const item = menuFrom(event.target);
      if (item) {
        hoveredMenu = item;
        clearTimeout(resumeTimer);
        setSuspended(true);
      } else if (suspended && !hoveredMenu?.contains(event.target)) scheduleResume();
    }, { signal });
    document.addEventListener('pointerout', event => {
      if (!suspended || !hoveredMenu?.contains(event.target) || hoveredMenu.contains(event.relatedTarget)) return;
      const next = menuFrom(event.relatedTarget);
      if (next) {
        hoveredMenu = next;
        clearTimeout(resumeTimer);
      } else scheduleResume();
    }, { signal });
    desktopMenu.addEventListener('change', () => {
      if (!desktopMenu.matches) {
        hoveredMenu = null;
        setSuspended(false);
      }
    }, { signal });

    // Capture before legacy details-modal and delegated theme handlers.
    window.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest(triggers);
      if (trigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        opened ? close() : open(trigger);
        return;
      }
      if (event.target.closest('[data-vna-search-close]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
      } else if (opened && !panel.contains(event.target)) close(false);
    }, { capture: true, signal });

    panel.addEventListener('click', event => {
      const keyword = event.target.closest('[data-vna-search-keyword]');
      if (!keyword) return;
      input.value = keyword.dataset.vnaSearchKeyword;
      form.requestSubmit();
    }, { signal });
    form.addEventListener('submit', event => {
      if (!input.value.trim()) { event.preventDefault(); input.focus(); }
    }, { signal });
    document.addEventListener('keydown', event => {
      if (!opened) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close(); return; }
      if (suspended) return;
      if (event.key !== 'Tab') return;
      const controls = [previousFocus, ...panel.querySelectorAll('button:not([disabled]), input:not([type="hidden"]), a[href]')]
        .filter(element => element?.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }, { capture: true, signal });
    window.addEventListener('resize', scheduleTop, { passive: true, signal });
    window.addEventListener('scroll', scheduleTop, { passive: true, signal });
    window.addEventListener('pagehide', () => close(false), { signal });
    window.addEventListener('pageshow', scheduleTop, { signal });
    document.addEventListener('shopify:section:load', () => {
      updateTriggers();
      scheduleTop();
      if (suspended && !hoveredMenu?.isConnected) { hoveredMenu = null; setSuspended(false); }
    }, { signal });
    const observer = new ResizeObserver(scheduleTop);
    document.querySelectorAll('.section-header-navigation, .section-header-mobile').forEach(header => observer.observe(header));
    updateTriggers();
    syncTop();
    body.setAttribute('data-vna-search-ready', '');
    window.VNASearchPanel = {
      open, close,
      destroy() {
        close(false);
        controller.abort();
        observer.disconnect();
        cancelAnimationFrame(topFrame);
        cancelAnimationFrame(focusFrame);
        body.removeAttribute('data-vna-search-ready');
        document.documentElement.style.removeProperty('--vna-search-header-bottom');
        originalAttributes.forEach((attributes, trigger) => {
          for (const [key, name] of [['expanded', 'aria-expanded'], ['controls', 'aria-controls'], ['label', 'aria-label']]) {
            attributes[key] === null ? trigger.removeAttribute(name) : trigger.setAttribute(name, attributes[key]);
          }
          trigger.querySelector('[data-vna-search-toggle-close]')?.remove();
        });
      }
    };
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
})();
